import React from 'react';
import { useSelector } from 'react-redux';
import { selectVisibleRisks } from './riskRegisterSlice';

export const RiskMatrix = () => {
    const risks = useSelector(selectVisibleRisks);

    // Flatten all risk items with their parent risk info
    const allRiskItems = risks.flatMap(risk =>
        (risk.items || []).map(item => ({
            ...item,
            riskCategory: risk.title
        }))
    );

    // Define color zones based on urgency (similar to the image)
    const getZoneColor = (probability, impact) => {
        const score = probability * impact;
        if (score >= 20) return '#ff0000'; // Red - Highest urgency
        if (score >= 15) return '#ff0000'; // Red
        if (score >= 12) return '#ffff00'; // Yellow
        if (score >= 9) return '#ffff00'; // Yellow
        if (score >= 6) return '#00ff00'; // Green
        if (score >= 3) return '#00ff00'; // Green
        return '#800080'; // Purple - Lowest urgency
    };

    const getZoneLabel = (probability, impact) => {
        const score = probability * impact;
        if (score >= 15) return 'Hi';
        if (score >= 9) return 'Med';
        return 'Lo';
    };

    // Create grid zones
    const renderGrid = () => {
        const zones = [];
        for (let impact = 5; impact >= 1; impact--) {
            for (let probability = 1; probability <= 5; probability++) {
                const x = (probability - 1) * 100;
                const y = (5 - impact) * 100;
                const color = getZoneColor(probability, impact);
                const score = probability * impact;

                zones.push(
                    <rect
                        key={`zone-${probability}-${impact}`}
                        x={x}
                        y={y}
                        width={100}
                        height={100}
                        fill={color}
                        opacity={0.3}
                        stroke="#999"
                        strokeWidth={1}
                    />
                );

                // Add score number in center of each zone
                zones.push(
                    <text
                        key={`score-${probability}-${impact}`}
                        x={x + 50}
                        y={y + 50}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="18"
                        fontWeight="bold"
                        fill="#333"
                        opacity={0.5}
                    >
                        {score}
                    </text>
                );
            }
        }
        return zones;
    };

    // Plot risk items
    const renderRiskPoints = () => {
        return allRiskItems.map((item, index) => {
            const x = (item.probability - 0.5) * 100;
            const y = (5 - item.impact + 0.5) * 100;
            const color = getZoneColor(item.probability, item.impact);

            return (
                <g key={item.id || index}>
                    <circle
                        cx={x}
                        cy={y}
                        r={8}
                        fill={color}
                        stroke="#000"
                        strokeWidth={2}
                        opacity={0.8}
                    />
                    <title>{`${item.title} (${item.riskCategory})\nImpact: ${item.impact}, Probability: ${item.probability}, Score: ${item.impact * item.probability}`}</title>
                </g>
            );
        });
    };

    return (
        <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginTop: '20px'
        }}>
            <h3 style={{ textAlign: 'center', marginBottom: '20px', color: '#dc3545' }}>
                Risk Matrix - Areas of Highest Warning Color
            </h3>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <svg width={550} height={550} style={{ border: '2px solid #333' }}>
                    {/* Background zones */}
                    {renderGrid()}

                    {/* Axis labels */}
                    {/* Y-axis label */}
                    <text
                        x={-250}
                        y={30}
                        transform="rotate(-90)"
                        textAnchor="middle"
                        fontSize="20"
                        fontWeight="bold"
                        fill="#333"
                    >
                        Urgency (Impact)
                    </text>

                    {/* X-axis label */}
                    <text
                        x={250}
                        y={540}
                        textAnchor="middle"
                        fontSize="20"
                        fontWeight="bold"
                        fill="#333"
                    >
                        Difficulty / Importance (Probability)
                    </text>

                    {/* Hi/Lo markers */}
                    <text x={250} y={20} textAnchor="middle" fontSize="24" fontWeight="bold">Hi</text>
                    <text x={250} y={520} textAnchor="middle" fontSize="24" fontWeight="bold">Lo</text>
                    <text x={20} y={260} textAnchor="middle" fontSize="24" fontWeight="bold">Lo</text>
                    <text x={480} y={260} textAnchor="middle" fontSize="24" fontWeight="bold">Hi</text>

                    {/* Grid numbers */}
                    {[1, 2, 3, 4, 5].map(num => (
                        <text key={`x-${num}`} x={(num - 0.5) * 100} y={515} textAnchor="middle" fontSize="16" fontWeight="bold">
                            {num}
                        </text>
                    ))}
                    {[5, 4, 3, 2, 1].map((num, idx) => (
                        <text key={`y-${num}`} x={15} y={(idx + 0.5) * 100 + 5} textAnchor="middle" fontSize="16" fontWeight="bold">
                            {num}
                        </text>
                    ))}

                    {/* Plot risk items */}
                    {renderRiskPoints()}
                </svg>
            </div>

            {/* Legend */}
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '20px', backgroundColor: '#ff0000', opacity: 0.5, border: '1px solid #333' }}></div>
                    <span><strong>Red Zone:</strong> High Urgency (Score ≥15)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '20px', backgroundColor: '#ffff00', opacity: 0.5, border: '1px solid #333' }}></div>
                    <span><strong>Yellow Zone:</strong> Medium Urgency (Score 9-14)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '20px', backgroundColor: '#00ff00', opacity: 0.5, border: '1px solid #333' }}></div>
                    <span><strong>Green Zone:</strong> Low-Medium Urgency (Score 3-8)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '20px', backgroundColor: '#800080', opacity: 0.5, border: '1px solid #333' }}></div>
                    <span><strong>Purple Zone:</strong> Low Urgency (Score &lt;3)</span>
                </div>
            </div>

            <div style={{ marginTop: '15px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '8px', textAlign: 'center' }}>
                <strong>Total Risks Plotted:</strong> {allRiskItems.length} items across {risks.length} categories
            </div>
        </div>
    );
};
